import * as React from 'react'
import * as z from 'zod'
import { toast } from 'sonner'
import { Button } from '~/sotano-kit/primitives/button'
import { Label, LabeledInput } from '~/sotano-kit/primitives/input'
import { clsx } from 'clsx'

export const InviteForm = ({
  children,
  label = 'Invite members',
  inputProps: _inputProps,
  ...props
}: Omit<React.JSX.IntrinsicElements['form'], 'onSubmit'> & {
  inputProps?: React.ComponentProps<typeof LabeledInput>['inputProps']
  label?: React.ComponentProps<typeof Label>['label']
  onSubmit?: (e: React.FormEvent<HTMLFormElement>, reset: () => void) => void
}) => {
  const { className, onChange, onKeyDown, ...inputProps } = _inputProps || {}
  const [inviteInputValue, setInviteInputValue] = React.useState('')

  const validateEmails = (value: string) => {
    if (!value) return true

    const emailList = value
      .split(',')
      .map((email) => email.trim())
      .filter(Boolean)

    const invalidEmails = emailList.filter(
      (email) => !z.string().email().safeParse(email).success
    )

    if (invalidEmails.length > 0) {
      toast.error(`Invalid email(s): ${invalidEmails.join(', ')}`)
      return false
    }

    return true
  }

  const inputClassName = clsx('!h-8 !pl-2', className)

  return (
    <form
      {...props}
      onSubmit={
        props.onSubmit
          ? (e) => {
              const success = validateEmails(inviteInputValue)
              if (!success) {
                e.preventDefault()
                return
              }
              props.onSubmit?.(e, () => {
                setInviteInputValue('')
                if (e.currentTarget) e.currentTarget.focus()
              })
            }
          : undefined
      }
    >
      <div className="relative flex-1">
        <LabeledInput
          blocked={false}
          label={label}
          name="emails"
          className={clsx(!label && '[&>span]:!pb-0')}
          inputProps={{
            value: inviteInputValue,
            required: true,
            type: 'text',
            autoFocus: true,
            placeholder: 'Emails, separated by commas',
            className: inputClassName,
            onKeyDown: (e) => {
              e.stopPropagation()
              if (onKeyDown) onKeyDown(e)
            },
            onChange: (e) => {
              if (onChange) onChange(e)
              setInviteInputValue(e.target.value)
            },
            ...inputProps,
          }}
        />
        {children}
      </div>

      <Button
        size="large"
        type="submit"
        className="ml-2"
        disabled={!inviteInputValue}
      >
        Invite
      </Button>
    </form>
  )
}
